import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { TbCalendarPlus, TbCheck, TbChevronLeft, TbChevronRight, TbClock, TbCreditCard, TbPencil } from 'react-icons/tb'
import { bookings, type Availability, type MentorshipService } from '../lib/content'
import { CTA, trackAttrs } from '../utils/track'
import Emoji from '../components/ui/Emoji/Emoji'
import InlineHighlight from '../components/content/InlineHighlight/InlineHighlight'

// Book a 1:1 session, on gazar.dev. This page is the whole reason the site
// stopped handing readers to an external scheduler: the offer, the calendar and
// the payment now live at the same address, so nobody bounces off a third-party
// booking page that knows nothing about the article they were just reading.
//
// The picker is a month calendar: only days with real openings are clickable,
// and choosing one reveals its times. Times come from the availability Gazar
// publishes in the admin, minus anything already taken; picking one holds it for
// twenty minutes while checkout happens. Paid sessions go straight to Stripe;
// the session is then confirmed by a person, and only that confirmation hands
// over the call link.

const HOLD_MINUTES = 20

// The steps have to branch on price, because the free session does not go
// through Stripe at all: `submit()` gets no `checkoutUrl` back and navigates
// straight to the booking's own page. Until now this list was a flat constant
// rendered unconditionally, so the free Discovery Call page told the reader
// "2. Pay" and "you're refunded in full" directly alongside "FREE", "No
// payment, ever" and "nothing is charged at any point". Every other string on
// this page already branched on `isFree`; this one was missed, and it was
// contradicting the four that got it right on the most-opened booking form on
// the site.
const howItWorks = (isFree: boolean) => [
  {
    icon: TbCalendarPlus,
    title: 'Pick a time',
    body: isFree
      ? 'These are the times that are genuinely open. Choosing one books it there and then.'
      : 'These are the times that are genuinely open. The one you pick is held for you for 20 minutes.',
  },
  isFree
    ? {
        icon: TbPencil,
        title: 'Say what you want from it',
        body: 'The last box on the form is what I prepare from, so the more concrete the better.',
      }
    : {
        icon: TbCreditCard,
        title: 'Pay',
        body: 'Through Stripe. Card details never touch this site.',
      },
  {
    icon: TbCheck,
    title: 'I confirm',
    body: isFree
      ? 'Within 24 hours, with the joining link. There is no card to enter and nothing is charged at any point.'
      : "Within 24 hours, with the joining link. If I can't make it work, you're refunded in full, automatically.",
  },
]

// Monday-first, to match how the UK reads a week.
const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

const pad = (n: number) => String(n).padStart(2, '0')
const dayKey = (y: number, m: number, d: number) => `${y}-${pad(m)}-${pad(d)}`
const monthIndex = (y: number, m: number) => y * 12 + (m - 1)

interface DaySlots {
  startsAt: string
  label: string
}

/**
 * Bucket the open start instants by calendar day, keyed and time-labelled in the
 * visitor's own timezone. The key is `YYYY-MM-DD` from en-CA, which is exactly
 * the string the calendar-grid cells are built with, so a London 18:30 slot
 * lands on whatever date it is for the person looking at it.
 */
function groupSlotsByDay(slots: { startsAt: string }[], timezone: string): Map<string, DaySlots[]> {
  const zone = timezone || 'UTC'
  const keyFmt = new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: zone })
  const timeFmt = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: zone })
  const map = new Map<string, DaySlots[]>()
  for (const slot of slots) {
    const date = new Date(slot.startsAt)
    if (Number.isNaN(date.getTime())) continue
    const key = keyFmt.format(date)
    const list = map.get(key) ?? []
    list.push({ startsAt: slot.startsAt, label: timeFmt.format(date) })
    map.set(key, list)
  }
  return map
}

type Cell = { y: number; m: number; d: number; key: string } | null

/** A month laid out as Monday-first weeks, for a plain wall calendar. */
function monthWeeks(year: number, month: number): Cell[][] {
  const first = new Date(Date.UTC(year, month - 1, 1))
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate()
  const lead = (first.getUTCDay() + 6) % 7 // shift Sun=0 → Mon=0
  const cells: Cell[] = Array.from({ length: lead }, () => null)
  for (let d = 1; d <= daysInMonth; d++) cells.push({ y: year, m: month, d, key: dayKey(year, month, d) })
  while (cells.length % 7 !== 0) cells.push(null)
  const weeks: Cell[][] = []
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7))
  return weeks
}

/** The day key `YYYY-MM-DD` rendered as "Thursday, 30 July". */
function dayKeyLabel(key: string): string {
  const [y, m, d] = key.split('-').map(Number)
  return new Intl.DateTimeFormat('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(y, m - 1, d, 12)))
}

export default function MentorshipBookPage({ service }: { service: MentorshipService }) {
  const navigate = useNavigate()
  const isFree = service.isFree || service.priceValue <= 0

  const [availability, setAvailability] = useState<Availability | null>(null)
  const [loadingSlots, setLoadingSlots] = useState(true)
  const [slotsError, setSlotsError] = useState('')
  const [chosen, setChosen] = useState('')

  // Calendar state: which month is on screen, and which day is opened.
  const [viewYM, setViewYM] = useState<{ y: number; m: number } | null>(null)
  const [selectedDay, setSelectedDay] = useState('')

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [company, setCompany] = useState('')
  const [link, setLink] = useState('')
  const [goal, setGoal] = useState('')
  const [timezone, setTimezone] = useState('')
  const [todayKey, setTodayKey] = useState('')
  const [status, setStatus] = useState<'idle' | 'loading' | 'error'>('idle')
  const [error, setError] = useState('')

  // The visitor's timezone is a browser fact, so it's read after mount. Reading
  // it during render would make the server and the client disagree about where
  // "here" is, which is a hydration mismatch on every load.
  useEffect(() => {
    try {
      setTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC')
    } catch {
      setTimezone('UTC')
    }
  }, [])

  // "Today" in the visitor's zone — computed after mount (a render-time Date
  // would disagree between server and client). Drives the Today jump and the
  // ring that marks the current day on the grid.
  useEffect(() => {
    if (!timezone) return
    try {
      setTodayKey(
        new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: timezone }).format(
          new Date(),
        ),
      )
    } catch {
      /* leave unset */
    }
  }, [timezone])

  // Availability is fetched client-side rather than in the loader on purpose:
  // it goes stale in minutes, and an SSR-cached calendar showing times that are
  // already gone is worse than a moment of "loading".
  useEffect(() => {
    let cancelled = false
    setLoadingSlots(true)
    bookings
      .availability(service.slug)
      .then((data) => {
        if (cancelled) return
        setAvailability(data)
        setSlotsError('')
      })
      .catch((err: Error) => {
        if (!cancelled) setSlotsError(err.message)
      })
      .finally(() => {
        if (!cancelled) setLoadingSlots(false)
      })
    return () => {
      cancelled = true
    }
  }, [service.slug])

  const slotsByDay = useMemo(
    () => (timezone ? groupSlotsByDay(availability?.slots ?? [], timezone) : new Map<string, DaySlots[]>()),
    [availability, timezone],
  )
  const availableKeys = useMemo(() => [...slotsByDay.keys()].sort(), [slotsByDay])

  // Open the earliest day with openings once availability lands (or after a
  // reload leaves the current pick empty). Everything the calendar navigates
  // between is derived from the days that actually have slots.
  useEffect(() => {
    if (availableKeys.length === 0) return
    if (selectedDay && slotsByDay.has(selectedDay)) return
    const first = availableKeys[0]
    const [y, m] = first.split('-').map(Number)
    setSelectedDay(first)
    setViewYM({ y, m })
  }, [availableKeys, selectedDay, slotsByDay])

  // A link from outside can name one time with ?slot=<ISO instant> (the MCP
  // server's booking_url does). Picked once, after the real availability lands,
  // and only if that time is still open; a gone time says so rather than
  // silently opening some other day. Declared after the effect above so its
  // choice of day wins on the same render.
  const [searchParams] = useSearchParams()
  const requestedSlot = searchParams.get('slot') ?? ''
  const [slotNotice, setSlotNotice] = useState('')
  const slotApplied = useRef(false)
  useEffect(() => {
    if (slotApplied.current || !requestedSlot || !availability || !timezone) return
    slotApplied.current = true
    const wanted = new Date(requestedSlot).getTime()
    const match = Number.isNaN(wanted) ? undefined : availability.slots.find((s) => new Date(s.startsAt).getTime() === wanted)
    if (!match) {
      setSlotNotice('The time you picked is no longer open. These are the times that still are.')
      return
    }
    const key = new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: timezone }).format(
      new Date(match.startsAt),
    )
    const [y, m] = key.split('-').map(Number)
    setSelectedDay(key)
    setViewYM({ y, m })
    setChosen(match.startsAt)
  }, [requestedSlot, availability, timezone])

  const monthBounds = useMemo(() => {
    if (availableKeys.length === 0) return null
    const toYM = (key: string) => {
      const [y, m] = key.split('-').map(Number)
      return monthIndex(y, m)
    }
    return { min: toYM(availableKeys[0]), max: toYM(availableKeys[availableKeys.length - 1]) }
  }, [availableKeys])

  const weeks = useMemo(() => (viewYM ? monthWeeks(viewYM.y, viewYM.m) : []), [viewYM])
  const daySlots = selectedDay ? (slotsByDay.get(selectedDay) ?? []) : []

  const monthLabel = viewYM
    ? new Intl.DateTimeFormat('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(
        new Date(Date.UTC(viewYM.y, viewYM.m - 1, 1)),
      )
    : ''
  const curIdx = viewYM ? monthIndex(viewYM.y, viewYM.m) : 0
  const canPrev = !!monthBounds && curIdx > monthBounds.min
  const canNext = !!monthBounds && curIdx < monthBounds.max

  function stepMonth(delta: number) {
    if (!viewYM) return
    const idx = curIdx + delta
    setViewYM({ y: Math.floor(idx / 12), m: (idx % 12) + 1 })
  }

  // Jump back to the current month, and open today if it happens to be bookable.
  function goToToday() {
    if (!todayKey) return
    const [y, m] = todayKey.split('-').map(Number)
    setViewYM({ y, m })
    if (slotsByDay.has(todayKey)) setSelectedDay(todayKey)
  }
  const todayYM = todayKey ? monthIndex(Number(todayKey.split('-')[0]), Number(todayKey.split('-')[1])) : null
  const showToday = todayYM !== null && todayYM !== curIdx

  const chosenLabel = useMemo(() => {
    if (!chosen || !timezone) return ''
    return new Intl.DateTimeFormat('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
      timeZone: timezone,
    }).format(new Date(chosen))
  }, [chosen, timezone])

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    if (!chosen) {
      setError('Pick a time first.')
      return
    }

    setStatus('loading')
    try {
      const booking = await bookings.create({
        service: service.slug,
        startsAt: chosen,
        name: name.trim(),
        email: email.trim(),
        timezone: timezone || 'UTC',
        company: company.trim() || undefined,
        link: link.trim() || undefined,
        goal: goal.trim(),
      })
      // Paid: straight to Stripe, slot held while they're in there. Free: there's
      // nothing to pay, so it goes to the booking's own page to wait on Gazar.
      if (booking.checkoutUrl) {
        window.location.href = booking.checkoutUrl
        return
      }
      navigate(`/mentorship/booking/${booking.reference}?new=1`)
    } catch (err) {
      setError((err as Error).message)
      setStatus('error')
      // A 409 means the slot went while they were typing. Reload what's left so
      // they're choosing from the truth rather than from a stale grid.
      bookings
        .availability(service.slug)
        .then((data) => {
          setAvailability(data)
          setChosen((current) => (data.slots.some((s) => s.startsAt === current) ? current : ''))
        })
        .catch(() => {})
    }
  }

  const inputCls =
    'w-full rounded-smooth border border-border-strong bg-white px-4 py-3 font-body text-body text-brand-navy focus:outline-none focus:border-brand-navy focus:ring-2 focus:ring-brand-navy/20'
  const labelCls = 'block font-heading text-overline uppercase tracking-wider text-ink-soft mb-1.5'

  return (
    <div className="bg-brand-cream min-h-screen">
      <section className="bg-brand-navy py-14 md:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* The colour goes on each <a>, not on the <ol>: a base rule in
              tailwind.css paints every anchor ink-strong, which on this navy
              hero renders the crumbs invisible if they only inherit. */}
          <nav aria-label="Breadcrumb" className="mb-6">
            <ol className="flex items-center gap-2 text-label font-body text-gray-50/60">
              <li>
                <Link to="/" className="text-gray-50/70 hover:text-gray-50 transition-colors">
                  Home
                </Link>
              </li>
              <li aria-hidden="true">/</li>
              <li>
                <Link to="/mentorship" className="text-gray-50/70 hover:text-gray-50 transition-colors">
                  Mentorship
                </Link>
              </li>
              <li aria-hidden="true">/</li>
              <li className="text-gray-50" aria-current="page">
                {service.title}
              </li>
            </ol>
          </nav>
          <h1 className="font-heading text-h1 font-bold text-gray-50 mb-4">
            Book a <InlineHighlight color="red">{service.title}</InlineHighlight>
          </h1>
          <p className="text-body-lg font-body text-gray-50/85 max-w-2xl">{service.description}</p>
          <p className="mt-6 inline-flex flex-wrap items-center gap-x-4 gap-y-2 font-body text-body-sm text-gray-50/75">
            {service.duration ? (
              <span className="inline-flex items-center gap-2">
                <TbClock aria-hidden="true" />
                {service.duration}
              </span>
            ) : null}
            <span className="font-heading text-gray-50">{isFree ? 'Free' : service.price}</span>
            <span>{isFree ? 'No payment, ever' : 'Refunded in full if I can’t make your time work'}</span>
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
        <div className="grid lg:grid-cols-[1.5fr_1fr] gap-10 lg:gap-14 items-start">
          <form onSubmit={submit} className="bg-white border border-border-subtle rounded-smooth-lg p-6 md:p-8">
            <h2 className="font-heading text-h3 font-bold text-brand-navy mb-1">Pick a time</h2>
            <p className="font-body text-body-sm text-ink-soft mb-6">
              {timezone ? (
                <>
                  All times are in <span className="font-heading text-brand-navy">{timezone}</span>, your computer's
                  timezone.
                </>
              ) : (
                'Loading times…'
              )}
            </p>

            {loadingSlots ? (
              <p className="font-body text-body-sm text-ink-muted mb-8">Checking what's open…</p>
            ) : slotsError ? (
              <p className="mb-8 rounded-smooth border border-brand-red/40 bg-brand-red/5 px-4 py-3 font-body text-body-sm text-brand-red">
                {slotsError}
              </p>
            ) : availableKeys.length === 0 ? (
              <div className="mb-8 rounded-smooth border border-border-strong p-5">
                <p className="font-heading text-body font-bold text-brand-navy mb-1">Nothing open right now</p>
                <p className="font-body text-body-sm text-ink-soft">
                  New times go up most weeks. If you're on a deadline,{' '}
                  <a href="mailto:me@gazar.dev" className="text-brand-navy underline underline-offset-2">
                    email me
                  </a>{' '}
                  and we'll find something.
                </p>
              </div>
            ) : (
              <div className="mb-8 grid sm:grid-cols-[auto_1fr] gap-6 sm:gap-8">
                {/* The month calendar. Only days with real openings are live. */}
                <div className="sm:w-[300px]">
                  <div className="flex items-center justify-between mb-1">
                    <button
                      type="button"
                      onClick={() => stepMonth(-1)}
                      disabled={!canPrev}
                      aria-label="Previous month"
                      className="grid place-items-center w-9 h-9 rounded-smooth border border-border-strong text-brand-navy hover:border-brand-navy disabled:opacity-30 disabled:hover:border-border-strong transition-colors"
                    >
                      <TbChevronLeft aria-hidden="true" />
                    </button>
                    <span className="font-heading text-body font-bold text-brand-navy">{monthLabel}</span>
                    <button
                      type="button"
                      onClick={() => stepMonth(1)}
                      disabled={!canNext}
                      aria-label="Next month"
                      className="grid place-items-center w-9 h-9 rounded-smooth border border-border-strong text-brand-navy hover:border-brand-navy disabled:opacity-30 disabled:hover:border-border-strong transition-colors"
                    >
                      <TbChevronRight aria-hidden="true" />
                    </button>
                  </div>
                  {/* Only shown once you've navigated off the current month, so it
                      never sits there doing nothing on first load. */}
                  <div className="h-6 flex justify-center mb-1">
                    {showToday ? (
                      <button
                        type="button"
                        onClick={goToToday}
                        className="font-heading text-caption uppercase tracking-wider text-brand-navy/70 hover:text-brand-navy transition-colors"
                      >
                        ← Today
                      </button>
                    ) : null}
                  </div>

                  <div className="grid grid-cols-7 gap-1 mb-1">
                    {WEEKDAY_LABELS.map((d) => (
                      <div key={d} className="text-center font-heading text-caption uppercase text-ink-muted py-1">
                        {d}
                      </div>
                    ))}
                  </div>

                  <div className="grid grid-cols-7 gap-1">
                    {weeks.flat().map((cell, i) => {
                      if (!cell) return <div key={`pad-${i}`} />
                      const has = slotsByDay.has(cell.key)
                      const isSelected = cell.key === selectedDay
                      const isToday = cell.key === todayKey
                      return (
                        <button
                          key={cell.key}
                          type="button"
                          disabled={!has}
                          onClick={() => setSelectedDay(cell.key)}
                          aria-pressed={isSelected}
                          aria-label={
                            dayKeyLabel(cell.key) + (isToday ? ' (today)' : '') + (has ? ', times available' : ', no times')
                          }
                          className={`relative h-10 rounded-smooth font-body text-body-sm transition-colors ${
                            isSelected
                              ? 'bg-brand-navy text-white font-heading'
                              : has
                                ? 'text-brand-navy hover:bg-brand-navy/5 border border-border-subtle font-heading'
                                : 'text-ink-muted/40'
                          } ${isToday && !isSelected ? 'ring-1 ring-brand-navy/40' : ''}`}
                        >
                          {cell.d}
                          {has && !isSelected ? (
                            <span className="absolute left-1/2 -translate-x-1/2 bottom-1 w-1 h-1 rounded-full bg-brand-red" />
                          ) : null}
                        </button>
                      )
                    })}
                  </div>
                  <p className="mt-3 font-body text-caption text-ink-muted">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-brand-red align-middle mr-1.5" />
                    Days with openings
                  </p>
                </div>

                {/* Times for the opened day. */}
                <div>
                  <p className="font-heading text-overline uppercase tracking-wider text-ink-muted mb-3">
                    {selectedDay ? dayKeyLabel(selectedDay) : 'Pick a day'}
                  </p>
                  {slotNotice ? (
                    <p role="status" className="mb-3 font-body text-body-sm text-brand-red">
                      {slotNotice}
                    </p>
                  ) : null}
                  {daySlots.length === 0 ? (
                    <p className="font-body text-body-sm text-ink-muted">No times left on this day.</p>
                  ) : (
                    <div className="flex flex-wrap gap-2 content-start">
                      {daySlots.map((slot) => {
                        const selected = chosen === slot.startsAt
                        return (
                          <button
                            key={slot.startsAt}
                            type="button"
                            onClick={() => setChosen(slot.startsAt)}
                            aria-pressed={selected}
                            className={`rounded-smooth border px-4 py-2 font-heading text-body-sm transition-colors ${
                              selected
                                ? 'border-brand-navy bg-brand-navy text-white'
                                : 'border-border-strong text-brand-navy hover:border-brand-navy'
                            }`}
                          >
                            {slot.label}
                          </button>
                        )
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {chosen ? (
              <p className="mb-8 rounded-smooth border border-border-subtle bg-brand-cream px-4 py-3 font-body text-body-sm text-brand-navy">
                <span className="font-heading">{chosenLabel}</span>
                {service.duration ? ` · ${service.duration}` : ''} · {isFree ? 'Free' : service.price}
              </p>
            ) : null}

            <h2 className="font-heading text-h3 font-bold text-brand-navy mb-1">Your details</h2>
            <p className="font-body text-body-sm text-ink-soft mb-6">No account needed.</p>

            <div className="grid sm:grid-cols-2 gap-5 mb-5">
              <div>
                <label className={labelCls} htmlFor="booking-name">
                  Your name
                </label>
                <input
                  id="booking-name"
                  className={inputCls}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  maxLength={120}
                  autoComplete="name"
                />
              </div>
              <div>
                <label className={labelCls} htmlFor="booking-email">
                  Email
                </label>
                <input
                  id="booking-email"
                  type="email"
                  className={inputCls}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  maxLength={200}
                  autoComplete="email"
                />
              </div>
              <div>
                <label className={labelCls} htmlFor="booking-company">
                  Company <span className="normal-case text-ink-muted">(optional)</span>
                </label>
                <input
                  id="booking-company"
                  className={inputCls}
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  maxLength={160}
                  autoComplete="organization"
                />
              </div>
              <div>
                <label className={labelCls} htmlFor="booking-link">
                  LinkedIn or GitHub <span className="normal-case text-ink-muted">(optional)</span>
                </label>
                <input
                  id="booking-link"
                  className={inputCls}
                  value={link}
                  onChange={(e) => setLink(e.target.value)}
                  maxLength={300}
                  placeholder="https://…"
                />
              </div>
            </div>

            <div className="mb-8">
              <label className={labelCls} htmlFor="booking-goal">
                What do you want out of this session?
              </label>
              <textarea
                id="booking-goal"
                className={`${inputCls} min-h-[140px]`}
                value={goal}
                onChange={(e) => setGoal(e.target.value)}
                required
                minLength={10}
                maxLength={2000}
                placeholder="The decision you're stuck on, the system you're designing, the role you're going for. Specifics make the session worth your money."
              />
              <p className="mt-1.5 font-body text-caption text-ink-muted">
                This is what I prepare from, so the more concrete the better.
              </p>
            </div>

            {error ? (
              <p
                role="alert"
                className="mb-5 rounded-smooth border border-brand-red/40 bg-brand-red/5 px-4 py-3 font-body text-body-sm text-brand-red"
              >
                {error}
              </p>
            ) : null}

            <button
              type="submit"
              disabled={status === 'loading' || !chosen}
              {...trackAttrs(CTA.bookingRequest, 'booking_form', { target: service.slug })}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-brand-navy text-white font-heading text-cta px-8 py-4 rounded-smooth hover:opacity-90 transition-opacity disabled:opacity-60"
            >
              {status === 'loading'
                ? isFree
                  ? 'Booking…'
                  : 'Taking you to Stripe…'
                : isFree
                  ? 'Book this call'
                  : `Continue to payment · ${service.price}`}
            </button>
            <p className="mt-3 font-body text-caption text-ink-muted">
              {isFree
                ? "Free, and nothing is charged at any point. I'll confirm within 24 hours."
                : `Your time is held for ${HOLD_MINUTES} minutes while you pay. I confirm within 24 hours, and if I can't make it work you're refunded in full.`}
            </p>
          </form>

          <aside className="lg:sticky lg:top-28 space-y-6">
            <div className="bg-white border border-border-subtle rounded-smooth-lg p-6">
              <div className="w-[110px] h-[110px] mb-5 flex items-center justify-center border border-border-subtle rounded-smooth overflow-hidden">
                <Emoji name={service.emoji || 'happy-energy'} className="w-full h-full" />
              </div>
              <h2 className="font-heading text-h4 font-bold text-brand-navy mb-1">{service.title}</h2>
              <p className="font-body text-body-sm text-ink-soft mb-4">{service.description}</p>
              <div className="flex items-center justify-between pt-4 border-t border-border-subtle">
                <span className="text-overline font-heading uppercase text-ink-muted">{service.duration}</span>
                <span className="font-heading text-h4 font-bold text-brand-navy">{isFree ? 'FREE' : service.price}</span>
              </div>
            </div>

            <div className="bg-white border border-border-subtle rounded-smooth-lg p-6">
              <h2 className="font-heading text-h4 font-bold text-brand-navy mb-5">How this works</h2>
              <ol className="space-y-5">
                {howItWorks(isFree).map((step, i) => {
                  const StepIcon = step.icon
                  return (
                    <li key={step.title} className="flex gap-3">
                      <span className="shrink-0 w-8 h-8 grid place-items-center rounded-smooth border border-border-subtle text-brand-navy">
                        <StepIcon aria-hidden="true" />
                      </span>
                      <div>
                        <p className="font-heading text-body-sm font-bold text-brand-navy">
                          {i + 1}. {step.title}
                        </p>
                        <p className="font-body text-caption text-ink-soft mt-0.5">{step.body}</p>
                      </div>
                    </li>
                  )
                })}
              </ol>
            </div>

            <div className="border border-border-subtle rounded-smooth-lg p-6">
              <p className="font-body text-caption text-ink-soft">
                Nothing here suits? See{' '}
                <Link to="/mentorship" className="text-brand-navy underline underline-offset-2">
                  all sessions
                </Link>
                , or{' '}
                <a href="mailto:me@gazar.dev" className="text-brand-navy underline underline-offset-2">
                  email me
                </a>{' '}
                and we'll find a time.
              </p>
            </div>
          </aside>
        </div>
      </section>
    </div>
  )
}
